/**
 * The docs page in English. Claude's menu names follow Claude's help on
 * custom connectors (checked 2026-10-21) and stay as Claude shows them.
 */

import type { DocsContent } from "./types";

export const en: DocsContent = {
  page: {
    title: "Docs",
    description: "How to use the DC Data connector in Claude.",
    contentsLabel: "Contents",
    pagerLabel: "Previous and next page",
    previous: "Previous",
    next: "Next",
  },

  pages: {
    "connect-claude": "Connect Claude",
    "what-you-can-ask": "What you can ask",
    tools: "The tools",
    limits: "What it cannot do",
    "privacy-and-data": "Privacy and data",
    troubleshooting: "Questions and problems",
  },

  connect: {
    intro:
      "Two steps: an Owner of De Ceuster’s Claude organization adds the connector once, for everyone. Then each person connects it with their own account.",
    introEveryoneFirst:
      "You connect the connector yourself, with your own account. If **DC Data** is not in your list yet, an Owner of De Ceuster’s Claude organization first adds it once, for everyone. Those steps are at the bottom.",
    modeOff:
      "The connector is not switched on yet. Once it is, these steps work and the URL to add shows here.",
    modeLocal:
      "This connector runs in local test mode, without sign-in, on this computer only. Add the URL to a local MCP client, such as Claude Code or the MCP Inspector.",
    ownerHeading: "Once, by an Owner of the Claude organization",
    ownerSteps: [
      "In Claude, open **Organization settings → Connectors**, click **Add**, then **Custom → Web**.",
      "Name it **DC Data** and paste this URL:",
      "Then click **DC Data** in the list and, under **Tool permissions**, set the **Read-only tools** group to **Always allow**. Every tool only reads, so lookups may then run without approval.",
    ],
    ownerFolded: "For the Owner of the Claude organization",
    everyoneHeading: "Then, for everyone",
    everyoneFirstHeading: "For everyone, with your own account",
    everyoneSteps: [
      "In Claude, in the browser or the app, open **Customize → Connectors**.",
      "Find **DC Data** and click **Connect**.",
      "Sign in with your De Ceuster Microsoft account, your normal work account.",
      "Then click **DC Data** and, under **Tool permissions**, set the **Read-only tools** group to **Always allow**, so Claude does not ask for permission with every question.",
      "Test it in a new chat: “Which account am I signed in to DC Data with?” Claude answers with your name and email address.",
    ],
    accessNote: "Everyone with a De Ceuster Microsoft account can sign in. Guest accounts cannot.",
    urlLoading: "Loading the URL…",
    urlNotOnYet: "The URL shows here once the connector is switched on.",
    urlFailed: "The URL could not be loaded. Reload the page to try again.",
  },

  copyUrl: {
    button: "Copy",
    copied: "Copied",
    label: "Copy the connector URL",
    copiedStatus: "Copied the connector URL.",
    failed: "Could not copy: select the URL and copy it yourself.",
  },

  capabilities: {
    intro:
      "The connector gives Claude read-only access to De Ceuster’s Odoo: clients, sites, cranes, orders, rental contracts, invoices, payments and revenue. Ask in plain words, in Dutch or English, as you would ask a colleague. Claude picks the tools itself and reads Odoo live, so you get the state of that moment.",
    toolsNote:
      "These are the tools you get in Claude. With most of them Claude reads De Ceuster’s Odoo. You do not need to know their names, because Claude chooses. They do help to see what Claude looked up. On the Tools page you switch a tool off for yourself, so Claude no longer uses it in your conversations.",
    toolsLink: "Your tools and whether they are on",
    toolsLoading: "Loading the tools…",
    toolsFailed: "The tools could not be loaded. Reload the page to try again.",
    toolSeparator: ": ",
    examplesHeading: "Try asking",
    quotes: ["“", "”"],
    examples: {
      revenue: "What was our revenue per month in 2025?",
      overdue: "Which invoices of [client] are overdue, and for how long?",
      "on-rent": "Which of our cranes are on rent right now, and with which clients?",
      rentals: "Which rental contracts are running at [client]?",
      deliveries: "Which orders are delivered next week, and on which site?",
      map: "Put our sites of the third quarter on a map, with rings around Geel.",
    },
    examplesNote: "Where it says [client], fill in a client’s name.",
    tipsHeading: "Tips",
    tips: {
      period: {
        title: "Name a period",
        body: "“In 2025”, “from 1 to 31 March”, “last month”. Without one, Claude searches broadly and the answer gets large.",
      },
      "client-first": {
        title: "First the client, then the details",
        body: "Let Claude find the client first and pick the right one when there are several. Then ask about its orders, rentals or invoices, so Claude works with that client and not a namesake.",
      },
      "say-what-you-mean": {
        title: "Say what you mean",
        body: "Claude shows confirmed orders and rental contracts; ask explicitly for quotes. Orders are searched by order date: say so when you mean the delivery date.",
      },
      form: {
        title: "Ask for the form you need",
        body: "A table, a chart, a map, or an artifact: an interactive page next to the chat.",
      },
    },
  },

  limits: {
    "changes-nothing": {
      title: "It changes nothing",
      body: "Claude cannot create or change orders, quotes, invoices or clients in Odoo through the connector, and it sends nothing.",
    },
    "not-everything": {
      title: "Not everything in Odoo",
      body: "Claude sees the payments that came in on the bank and the invoices they settled. A payment not yet matched to an invoice is counted apart. Such an invoice still shows as open in Odoo, although it may already be paid. The ownership history of cranes, and leasing and insurance per crane are not shown.",
    },
    "map-approximate": {
      title: "Positions on a map are approximate",
      body: "Odoo keeps no coordinates for sites. Claude places a site at the centre of its postcode, or of its municipality when the postcode is missing, and measures the distance to Geel as the crow flies. Sites abroad, or without a usable postcode or municipality, stay off the map.",
    },
    "closed-rental-site": {
      title: "The site of a closed rental",
      body: "Odoo sometimes sets the site of a closed rental contract back to your depot. Claude then takes the site of the last invoice.",
    },
    "no-end-date": {
      title: "No actual end date of a rental",
      body: "Odoo knows the start, the estimated end and the day the contract was closed in Odoo, not the day the crane actually came back. “Which cranes were on rent last month” is a good approximation, not an exact count.",
    },
    "occupancy-per-type": {
      title: "Occupancy and availability per crane type",
      body: "Claude gives the occupancy rate (bezettingsgraad) per day, week or month from 1 January 2024, for the whole fleet, for the self-erecting and the tower cranes, and per crane type. From today on, Claude counts the booked cranes, up to a year ahead. Claude also says which cranes are free in a period and which alerts the Occupancy app raises. Claude computes all of this from Odoo when you ask, with the rules of the Occupancy app, and says when it read Odoo. Claude reuses one read of Odoo for at most 10 minutes, so a booking made just now can take a few minutes to count. When Odoo does not show the cranes, Claude says so and gives no estimate. The height and jib of a crane are not in Odoo, so availability goes by crane type, not by size.",
    },
    "finance-checks": {
      title: "Unbilled work and transport prices",
      body: "Claude can show work that was delivered but not, or only partly, invoiced, and transports invoiced far under the tariff. Claude computes both from Odoo when you ask, and says when it read Odoo. Claude reuses one read of Odoo for at most 10 minutes, so an invoice made just now can take a few minutes to count. Work that looks billed another way, for example through a credit note, another invoice or the sale of a crane, is left out. Work on your own rental cranes does not count in what clients may still owe and is listed apart. Odoo holds no transport costs, so a price under the tariff is no loss. Client names only reach people with Clients.",
    },
    "outside-odoo": {
      title: "Nothing outside Odoo",
      body: "The planning in Visual Planner, the Ford app, your mail, and why a quote was lost.",
    },
    "left-out": {
      title: "Left out on purpose",
      body: "Contact details (email, phone), bank details, HR data and internal notes in Odoo.",
    },
    "answer-size": {
      title: "A limited size per answer",
      body: "Each lookup fetches a limited amount. When an answer is too large it is cut short, and Claude says so.",
    },
  },

  privacy: {
    "own-account": {
      title: "Your own account",
      body: "Everyone signs in with their own De Ceuster Microsoft account. When someone leaves, De Ceuster’s IT administrator disables their account, and their access ends within about an hour. When someone no longer needs the connector, or their access has to end sooner, an admin blocks them at once under People and teams, on the Teams tab. Velta can do it for you too.",
    },
    "same-for-everyone": {
      title: "The same tools, the same data",
      body: "The connector reads Odoo through one read-only account, so what someone sees does not follow their own rights in Odoo. With the same tools, everyone sees the same Odoo data, such as the Companyweb score or a crane’s minimum price and margin. Open invoices per client only reach people who may search invoices, through Finance in their team or a personal exception. In the same way, the occupancy tools and the tools for unbilled work and transport prices show client names only to people with Clients.",
    },
    "passes-through": {
      title: "Data passes through",
      body: "The connector passes Odoo data on to Claude and does not store it. Only your sign-in is kept, encrypted, so you do not have to sign in every time. The answer then sits in your chat in Claude, like everything else you do in Claude.",
    },
    "log-metadata": {
      title: "The log keeps metadata only",
      body: "Who used which tool, when, how long it took and whether it failed, to keep the service running. The log keeps this for 12 months and then deletes it automatically. What was asked or answered is never stored or logged.",
    },
  },

  roles: {
    heading: "Who sees what on the dashboard",
    intro:
      "Everyone has one role on this dashboard. It is apart from their role in your Claude organization. At their first sign-in, everyone is a Member. An admin can then change their role under People and teams.",
    access: "Access",
    yes: "Yes",
    no: "No",
    rows: {
      connector: "The DC Data connector in Claude, with your team’s tools",
      "start-and-docs": "Home and Docs",
      apps: "The apps your team has, such as Occupancy",
      "own-switches": "Switching a tool off for yourself, on the Tools page",
      "admin-pages":
        "Roles, teams and blocking someone under People and teams, who gets which tool on Tools, Analytics and Activity log",
      "issue-notes":
        "Reading an issue’s history under Failures in Analytics and adding a note to it",
      roadmap: "Following the roadmap and proposing ideas for it, with each month’s progress",
      "roadmap-budget":
        "Seeing each month’s budget and the hours and days of the work on the roadmap, and changing the budget for this month and later",
      "roadmap-work":
        "Adding ideas straight to the roadmap, reviewing the ideas and questions in Triage, setting the effort of roadmap items, moving items and seeing the Linear tickets",
      "issue-work":
        "Taking on, resolving, ignoring or reopening an issue under Failures in Analytics, and working on the issues from Claude",
      "pause-tools": "Pausing a tool for everyone, on the Tools page",
      "view-as":
        "Viewing the dashboard as a colleague or a role, without being able to change anything, under View as in Settings",
    },
    note: "Admins, owners and developers are always in the Admins team, which has every tool and every app.",
  },

  faq: {
    "not-listed": {
      question: "DC Data is not in my list of connectors",
      answer: [
        "An Owner of your Claude organization adds the connector once for everyone (see [[connect-claude]]). Ask them to add it.",
      ],
    },
    "sign-in-fails": {
      question: "Signing in fails",
      answer: [
        "Sign in with your own De Ceuster Microsoft account, your normal work account. Guest accounts and accounts of other organizations are refused. If it still fails, ask an admin or Velta whether your account is blocked, or ask De Ceuster’s IT administrator whether it is still active.",
      ],
    },
    "not-from-odoo": {
      question: "Claude answers, but not from Odoo",
      answer: [
        "An answer from Odoo shows a DC Data step that you can open to see what Claude looked up. Without it, the answer did not come from Odoo. Check that the connector is switched on for the chat, in the tools menu under the message box.",
      ],
    },
    "cut-short": {
      question: "The answer was cut short",
      answer: [
        "Make the question smaller: a shorter period, one client, one crane or one type of order. Or ask for a summary (revenue per month) instead of every invoice.",
      ],
    },
    slow: {
      question: "It takes a long time, or Odoo is too busy",
      answer: [
        "Odoo answers at a limited pace, so a large question can take a minute or more, and longer when several colleagues ask at once. When Claude says Odoo was too slow or too busy, try again after a minute.",
      ],
    },
    "number-wrong": {
      question: "A number looks wrong",
      answer: [
        "Check important figures in Odoo before you pass them on or decide on them.",
        [
          "Revenue and order amounts exclude VAT. An invoice’s total and what is still open or overdue include VAT. Claude says which one it gives.",
          "Revenue leaves out what you invoice to De Ceuster’s own companies; the map and the client list leave those companies out too.",
          "The data in Odoo starts in mid-2023: compare years from 2024.",
        ],
      ],
    },
    "how-looked-up": {
      question: "How did Claude look it up?",
      answer: [
        "Ask it: “Which period and filters did you use?” You see straight away whether Claude understood the question.",
      ],
    },
  },
};
